/**
 * paperStats — 담긴 문항 → 시험지 통계. **순수 함수다**(React 를 모른다).
 *
 * 떼어 둔 이유는 검산이다. 좌측 패널이 「대수 12」라고 말할 때 그 12가 무엇을 센 값인지
 * 화면 코드 사이에서 읽어 내려면 렌더 로직을 함께 헤쳐야 한다. 여기 있으면 입력과 출력만 본다.
 *
 * ── 왕복은 늘지 않는다 ────────────────────────────────────────────────────
 * 재료는 **이미 부르고 있던 응답**이다. 개념 배지를 채우려고 치던 problem-search 를 본문 없이
 * 전 문항으로 넓힌 것(fetchProblemItemsV2)과, 화면 여럿이 나눠 쓰는 개념 사전(5분 모듈 캐시)뿐이다.
 * 배포 건수·판 이력처럼 왕복이 느는 값은 **여기 없다** — 그 둘은 옆 탭이 정본이다.
 *
 * ── 무엇을 세는가 ─────────────────────────────────────────────────────────
 * 난이도·정답/풀이 미비는 **문항 단위**(문항당 하나뿐이라 셈이 곧 문항 수다).
 * 영역·개념 유형은 **개념 태그 단위**다 — 한 문항이 개념을 여러 개 달고 그 개념들이 서로 다른
 * 영역에 걸치기 때문이다. 문항 단위로 세려면 「대표 영역」을 발명해야 하는데, 그 규칙은
 * 이 저장소 어디에도 없다. 그래서 화면이 「개념 태그 N개 기준」이라고 밝히고 태그를 센다.
 *
 * ── 못 받은 문항 ─────────────────────────────────────────────────────────
 * 메타를 못 받은 문항은 분포에서 **빠진다**. 그 수를 `missing` 으로 함께 돌려주는 것이 요점이다 —
 * 200문항 중 40개가 빠진 채 「대수 12」라고만 적으면 화면이 조용히 거짓말을 한다.
 */
import { MATH_TYPE_LABEL, CONCEPT_TYPE_LABEL } from '../common/report/reportColors';
import { normalizeConceptNames } from '../ProblemHubV2/problemApi';

/** 영역 나열 순서 — 리포트 라벨 표의 선언 순서를 그대로 따른다(같은 값에 다른 순서를 만들지 않는다) */
export const MATH_TYPE_ORDER = Object.keys(MATH_TYPE_LABEL);

/**
 * 문항의 개념 쌍 뽑기 — 계약이 `concept_names=[{concept_id,name}]` 로 통일됐지만(fe-handover)
 * 문자열 배열로 오는 응답도 있어 `concept_ids` 와 자리로 맞추는 길을 남긴다.
 * (PaperItemsEditor 의 toConceptPairs 와 같은 규칙 — 같은 응답을 두 규칙으로 읽지 않는다.)
 */
export function conceptPairsOf(src) {
  const names = src?.concept_names ?? src?.concepts ?? src?.concept_name ?? null;
  if (Array.isArray(names) && names.length > 0 && typeof names[0] === 'object') {
    return names
      .map((c) => ({ id: c?.concept_id ?? c?.id ?? null, name: c?.name ?? c?.text ?? '' }))
      .filter((c) => c.name);
  }
  const idArr = Array.isArray(src?.concept_ids) ? src.concept_ids : [];
  // 문자열 배열·맵({"425":"마름모"}) 둘 다 흡수한다 — 이 계열 API 가 concept_names 를
  // 세 형태로 주기 때문이다(problemApi.normalizeConceptNames 의 주석에 그 셋이 적혀 있다).
  const nameArr = normalizeConceptNames(names);
  if (nameArr.length > 0) return nameArr.map((name, i) => ({ id: idArr[i] ?? null, name }));
  // 이름이 아예 없으면 id 만으로 — 사전이 이름을 채운다
  return idArr.map((id) => ({ id, name: '' }));
}

/**
 * 통계 한 벌.
 *
 * @param rows      편집 중인 행(PaperItemsEditor 의 rows). **순서·개수의 정본이다** —
 *                  사용자가 방금 뺀 행은 서버가 아직 모르므로 서버 수치를 믿으면 안 된다.
 * @param metaMap   `{ [String(pid)]: item|null }`. null = 물어봤는데 못 받음.
 *                  키 자체가 없으면 아직 안 물어본 것(pending).
 * @param dictIndex `Map(String(concept_id) → 사전항목)`. 없으면 영역·유형 분포를 내지 않는다.
 */
export function buildPaperStats({ rows = [], metaMap = {}, dictIndex = null } = {}) {
  const total = rows.length;
  const deleted = rows.filter((r) => r.isDeleted).length;

  const difficulty = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  let difficultyNone = 0;   // 난이도가 없는 문항 — 배포가 409 로 막히는 그 문항이다
  let missing = 0;          // 물어봤는데 못 받은 문항(분포에서 빠진다)
  let pending = 0;          // 아직 안 물어본 문항
  const need = { concept: 0, answer: 0, solution: 0 };

  const mathCount = new Map();     // math_type → 개념 태그 수
  const typeCount = new Map();     // concept_type → 개념 태그 수
  const conceptCount = new Map();  // concept key → {id, name, count, order, type}
  let conceptTagTotal = 0;
  let dictMiss = 0;                // 사전에서 못 찾은 개념 태그 수

  rows.forEach((r) => {
    const key = r.problem_id == null ? '' : String(r.problem_id);
    const meta = key === '' ? null : metaMap[key];
    const known = meta != null;
    if (key !== '' && !(key in metaMap)) pending += 1;
    else if (!known) missing += 1;

    // 난이도 — 상세가 실어 준 값이 먼저다(사용자가 방금 담은 행은 담을 때 들고 온 값이 있다)
    const d = r.difficulty ?? (known ? meta.difficulty : null);
    if (d != null && difficulty[d] != null) difficulty[d] += 1;
    else if (known || r.difficulty != null) difficultyNone += 1;

    if (known) {
      // need_* 는 검색 응답이 실어 주는 「분석이 덜 됐다」 표시다(계약 §1-5).
      // 개념은 빈 배열도 '필요'로 세므로 배지가 없을 때만 직접 센다.
      if (meta.need_concept) need.concept += 1;
      if (meta.need_answer) need.answer += 1;
      if (meta.need_solution) need.solution += 1;
    }

    // 개념 태그 — 행이 이미 들고 있는 것이 먼저, 없으면 메타에서
    const pairs = (r.concepts?.length ? r.concepts : (known ? conceptPairsOf(meta) : []));
    pairs.forEach((c) => {
      const dictItem = dictIndex && c.id != null ? dictIndex.get(String(c.id)) : null;
      const name = c.name || dictItem?.concept_name || dictItem?.name || '';
      if (name === '' && c.id == null) return; // 이름도 id 도 없으면 셀 것이 없다
      conceptTagTotal += 1;

      const ck = c.id != null ? `id:${c.id}` : `nm:${name}`;
      const prev = conceptCount.get(ck);
      if (prev) prev.count += 1;
      else {
        conceptCount.set(ck, {
          id: c.id ?? null,
          name,
          count: 1,
          order: dictItem?.order ?? null,
          type: dictItem?.concept_type ?? null,
        });
      }

      if (dictIndex) {
        if (dictItem == null) dictMiss += 1;
        else {
          const mt = dictItem.math_type || '_';
          mathCount.set(mt, (mathCount.get(mt) || 0) + 1);
          const ct = dictItem.concept_type;
          if (ct) typeCount.set(ct, (typeCount.get(ct) || 0) + 1);
        }
      }
    });
  });

  // 영역 — **고정 순서**로 낸다. 개수 순으로 흔들리면 시험지를 옮길 때마다 막대가 자리를 바꿔
  // 두 시험지를 눈으로 견줄 수 없다. 0 인 영역도 남긴다(없다는 것이 정보다).
  const mathTypes = MATH_TYPE_ORDER.map((code) => ({
    code,
    label: MATH_TYPE_LABEL[code],
    count: mathCount.get(code) || 0,
  }));
  const otherMath = mathCount.get('_') || 0;
  if (otherMath > 0) mathTypes.push({ code: '_', label: '기타', count: otherMath });

  // 개념 유형 — 여덟 종이라 전부 세우면 좁은 열에서 목록이 된다. 0 인 것은 빼고 많은 순으로.
  const conceptTypes = Array.from(typeCount.entries())
    .map(([code, count]) => ({ code, label: CONCEPT_TYPE_LABEL[code] || code, count }))
    .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));

  /**
   * 개념 — **많이 나온 순으로 전부** 돌려준다(오너 지시 2026-08-28 「내림차순으로 개념별 정렬」).
   * 여기서 자르지 않는 것이 요점이다: 몇 줄이 들어가는지는 열의 높이가 정하는 일이고,
   * 그 높이는 화면만 안다. 집계가 미리 다섯 개로 잘라 두면 화면은 여섯 줄이 들어가는 자리에도
   * 다섯 줄밖에 못 세운다.
   *
   * 같은 수면 순번(교육과정 전역 정렬)으로, 순번도 없으면 이름으로 가른다 — 순서를 끝까지
   * 확정해야 같은 시험지를 다시 열었을 때 목록이 흔들리지 않는다.
   */
  const concepts = Array.from(conceptCount.values())
    .sort((a, b) => b.count - a.count
      || (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
      || a.name.localeCompare(b.name));

  const known = total - missing - pending;
  return {
    total,
    deleted,
    known,        // 메타를 손에 쥔 문항 수 — 분포의 모수다
    missing,
    pending,
    difficulty,
    difficultyNone,
    difficultyKnown: Object.values(difficulty).reduce((a, b) => a + b, 0),
    need,
    mathTypes,
    conceptTypes,
    concepts,
    conceptTagTotal,
    uniqueConcepts: conceptCount.size,
    dictMiss,
    dictReady: dictIndex != null,
  };
}

export default buildPaperStats;
